<template>
  <ContractReviewDisplayComponent
    :accountData="accountData"
    :contractsToShow="contracts"
    :signerFields="signerFields"
    :companyFields="companyFields"
    :generatorFields="generatorFields"
    :contractState="contractState"
    :hasGenerator="hasGenerator"
    @createContainerForStartSigningReady="
      $emit('createContainerForStartSigningReady', $event)
    "
    @disableChangingInformation="$emit('disableChangingInformation')"
  />
</template>

<script setup lang="ts">
import { useCompanyFinder } from "@/composables/useCompanyFinder";
import { Account } from "@/types";
import { useOptionalContractInclusion } from "@/signup/composables/useOptionalContractInclusion";
import { reactive, computed } from "vue";
import ContractReviewDisplayComponent from "@/signup/ContractReviewDisplayComponent.vue";
import { useI18n } from "vue-i18n";
import {
  EN_AUTHORIZATION_APPLICATION,
  EN_SOLDERA_MEMBERSHIP,
} from "@/signup/contract_keys";

const props = defineProps<{
  accountData: Account;
}>();

defineEmits<{
  createContainerForStartSigningReady: [
    createContainerForStartSigning: () => Promise<void>,
  ];
  disableChangingInformation: [];
}>();

const { t } = useI18n();
const signerFields = reactive([
  { label: t("account.name"), value: props.accountData.name },
  { label: t("account.email"), value: props.accountData.email },
  {
    label: t("account.phone_number"),
    value: props.accountData.phone_number,
  },
]);
const { companyIndex } = useCompanyFinder(props.accountData);
const selectedCompany = computed(
  () => props.accountData.companies[companyIndex.value]
);
const companyFields = reactive([
  { label: t("company.name"), value: selectedCompany.value.name },
  {
    label: t("company.registry_code"),
    value: selectedCompany.value.registry_code,
  },
  { label: t("company.vat"), value: selectedCompany.value.vat },
  { label: t("company.address"), value: selectedCompany.value.address },
]);

// Empty generator fields since ES doesn't use generators
const generatorFields = reactive([]);
const hasGenerator = computed(() => false);

const contractState = computed(() => 1); // Always in authorization mode

const { includeDocument } = useOptionalContractInclusion(
  selectedCompany,
  contractState
);

// Build contracts list
const contractsList = [
  {
    id: 1,
    key: EN_AUTHORIZATION_APPLICATION,
    contract_title: t(
      "signup.contract_review_component.es.es_account_authorization_form_title"
    ),
    contract_description: t(
      "signup.contract_review_component.es.es_account_authorization_form_text"
    ),
    include: includeDocument([1], EN_AUTHORIZATION_APPLICATION).value,
    can_include: true,
  },
  {
    id: 2,
    key: EN_SOLDERA_MEMBERSHIP,
    contract_title: t(
      "signup.contract_review_component.es.es_soldera_membership_agreement_title"
    ),
    contract_description: t(
      "signup.contract_review_component.es.es_soldera_membership_agreement_text"
    ),
    include: includeDocument([1], EN_SOLDERA_MEMBERSHIP).value,
    can_include: true,
  },
];

const contracts = reactive([[...contractsList]]);
</script>
